import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import EmptyData from '../../../components/UI/EmptyData';

import { buildCelebrationRows, CelebrationPersonCard } from './CelebrationHumanList';

import './CelebrationSnapSlider.scss';

/**
 * One full-width card per “slide”, soonest-first order from parent (e.g. celebrationsWithDaysLeft).
 * Scrollbar hidden; prev/next move exactly one card.
 */
const CelebrationSnapSlider = ({ variant = 'birthday', items = [] }) => {
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);

  const rows = useMemo(() => buildCelebrationRows(items, variant), [items, variant]);

  const updateScrollState = useCallback(() => {
    const el = trackRef.current;
    if (!el || !rows.length) {
      setIndex(0);
      return;
    }
    const w = el.clientWidth;
    if (!w) return;
    const i = Math.round(el.scrollLeft / Math.max(w, 1));
    setIndex(Math.min(Math.max(i, 0), rows.length - 1));
  }, [rows.length]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateScrollState) : null;
    if (ro) ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      if (ro) ro.disconnect();
    };
  }, [updateScrollState, rows.length]);

  const scrollByPage = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const w = el.clientWidth;
    el.scrollBy({ left: dir * w, behavior: 'smooth' });
  };

  if (!rows.length) {
    return (
      <div className="celebration-snap-slider celebration-snap-slider--empty">
        <EmptyData />
      </div>
    );
  }

  const label =
    variant === 'birthday'
      ? 'Birthdays'
      : variant === 'anniversary'
        ? 'Anniversaries'
        : 'Recent hiring';
  const atStart = index <= 0;
  const atEnd = index >= rows.length - 1;

  return (
    <div className="celebration-snap-slider" aria-label={`${label}, carousel`}>
      {rows.length > 1 ? (
        <div className="celebration-snap-slider__toolbar">
          <span className="celebration-snap-slider__counter" aria-live="polite">
            {index + 1} / {rows.length}
          </span>
          <div className="celebration-snap-slider__arrows">
            <button
              type="button"
              className="celebration-snap-slider__btn"
              aria-label="Previous"
              disabled={atStart}
              onClick={() => scrollByPage(-1)}
            >
              <i className="icofont icofont-rounded-left" aria-hidden />
            </button>
            <button
              type="button"
              className="celebration-snap-slider__btn"
              aria-label="Next"
              disabled={atEnd}
              onClick={() => scrollByPage(1)}
            >
              <i className="icofont icofont-rounded-right" aria-hidden />
            </button>
          </div>
        </div>
      ) : null}

      <div
        ref={trackRef}
        className="celebration-snap-slider__track"
        role="region"
        tabIndex={0}
        aria-roledescription="carousel"
      >
        {rows?.map((r) => (
          <div key={r?.key} className="celebration-snap-slider__slide">
            <CelebrationPersonCard variant={variant} {...r} />
          </div>
        ))}
      </div>

      {rows.length > 1 ? (
        <div className="celebration-snap-slider__dots" aria-hidden>
          {rows.map((r, i) => (
            <span
              key={r.key}
              className={`celebration-snap-slider__dot ${i === index ? 'is-active' : ''}`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default CelebrationSnapSlider;
